import React, { useRef, useState } from 'react';

import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import { Checkbox } from '@atlaskit/checkbox';
import Heading from '@atlaskit/heading';
import { Radio } from '@atlaskit/radio';
import TextField from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import { updateAccountsMapping } from 'src/services/accountsMapping';
import { getJiraContext, useJiraContext } from 'src/services/jira';

import ErrorMessage from 'src/views/components/ErrorMessage';
import Field from 'src/views/components/Form/Field';

import { JiraCredentialsValidation } from './components/CredentialsValidation';
import ValidationMessage from './components/ValidationMessage';

function valuesFromAccountsMapping(accountsMapping) {
  const jiraConfig = accountsMapping.jira || {};
  const jiraAuth = jiraConfig.auth || {};
  const jiraOptions = jiraConfig.options || {};

  return {
    api_base_url: jiraConfig.api_base_url || '',
    browse_base_url: jiraConfig.browse_base_url || '',
    ignore_ssl_errors: jiraOptions.ignore_ssl_errors || false,
    auth_type: jiraAuth.type || 'basic',
    username: jiraAuth.username || '',
    password: jiraAuth.password || '',
    pat: jiraAuth.pat || '',
  };
}

function addDefaults(values) {
  values = { ...values };
  if (!values.browse_base_url) {
    values.browse_base_url = getJiraContext().context.browseBaseUrl || '';
  }
  if (values.password) {
    values.password = '';
  }
  if (values.pat) {
    values.pat = '';
  }
  return values;
}

function isConfigured({ api_base_url, browse_base_url, auth_type, username, password, pat }) {
  return api_base_url && browse_base_url && ((auth_type === 'basic' && username && password) || (auth_type === 'pat' && pat));
}

function buildUpdateBody(values, passwordTouched) {
  let auth = { type: values.auth_type };
  if (auth.type === 'basic') {
    auth.username = values.username;
    auth.password = passwordTouched ? values.password : undefined;
  } else if (auth.type === 'pat') {
    auth.pat = values.pat;
  }
  return {
    jira: {
      api_base_url: (values.api_base_url || '').trim(),
      browse_base_url: (values.browse_base_url || '').trim(),
      options: {
        ignore_ssl_errors: values.ignore_ssl_errors,
      },
      auth
    }
  };
}

function validateRequiredFields(values) {
  const requiredFields = ['api_base_url'];
  if (values.auth_type === 'basic') {
    requiredFields.push('username', 'password');
  } else if (values.auth_type === 'pat') {
    requiredFields.push('pat');
  }
  return requiredFields.filter((field) => !values[field] || values[field].toString().trim() === '');
}

const JiraCredentialsForm = ({ accountsMapping }) => {
  const ref = useRef(null);
  const [lastSavedValue, setLastSavedValue] = useState(valuesFromAccountsMapping(accountsMapping));

  const [editing, setEditing] = useState(!isConfigured(lastSavedValue));
  const [values, setValues] = useState(editing ? addDefaults(lastSavedValue) : lastSavedValue);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [requiredFieldsMissing, setRequiredFieldsMissing] = useState(false);
  const [error, setError] = useState(null);
  const { session: { name: currentJiraUserName } = {} } = useJiraContext();
  const usernameSameAsCurrent = values.username && values.username === currentJiraUserName;

  const blurAll = () => {
    const all = (ref.current && ref.current.querySelectorAll && ref.current.querySelectorAll('input')) || [];
    all.forEach((item) => item && item.blur && item.blur());
  };

  const onCancel = () => {
    blurAll();
    setValues(lastSavedValue);
    setEditing(false);
    setPasswordTouched(false);
    setError(null);
    setRequiredFieldsMissing(false);
  };

  const onEdit = () => {
    setEditing(true);
    setValues(addDefaults(values));
  };

  const onSave = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setSaving(true);
    setError(null);
    // Validate required fields
    const missingFields = validateRequiredFields(values);
    if (missingFields.length > 0) {
      setRequiredFieldsMissing(true);
      setSaving(false);
      return;
    }
    const body = buildUpdateBody(values, passwordTouched);
    updateAccountsMapping(body)
      .then((res) => {
        blurAll();
        setLastSavedValue(valuesFromAccountsMapping(res));
        setValues(valuesFromAccountsMapping(res));
        setSaving(false);
        setRequiredFieldsMissing(false);
        setEditing(false);
        setPasswordTouched(false);
      })
      .catch((e) => {
        setSaving(false);
        setError(e);
      });
  };

  const onChange = (e) => {
    const { name, value, checked, type } = e.target;
    if (type === 'checkbox') {
      setValues({ ...values, [name]: checked });
    } else if (type === 'radio') {
      setError(null);
      setRequiredFieldsMissing(false);
      setValues({ ...values, [name]: value });
    } else {
      if (name === 'password') setPasswordTouched(true);
      setValues({ ...values, [name]: value });
    }
  };

  return (
    <div ref={ref} data-cy="jira-account-settings-form">
      <Heading size="small">Jira Connection options</Heading>
      <form onSubmit={onSave}>
        <Field label="Jira Base URL" isRequired>
          <TextField
            isDisabled={!editing}
            isCompact
            value={values.api_base_url}
            onChange={onChange}
            name="api_base_url"
          />
        </Field>

        <Field label="Jira Browse Base URL">
          <TextField
            isDisabled={!editing}
            isCompact
            value={values.browse_base_url}
            onChange={onChange}
            name="browse_base_url"
          />
        </Field>

        <Field>
          <Checkbox
            label="disable SSL security (not recommended)"
            isDisabled={!editing}
            isChecked={values.ignore_ssl_errors}
            onChange={onChange}
            name="ignore_ssl_errors"
          />
        </Field>


        <div style={{ display: 'flex', gap: 24, alignItems: 'center', margin: '12px 0' }}>
          <Radio
            value="basic"
            label="Basic authentication"
            name="auth_type"
            testId="radio-auth"
            isDisabled={!editing}
            isChecked={values.auth_type === 'basic'}
            onChange={onChange}
          />
          <Radio
            value="pat"
            label="Personal Access Token"
            name="auth_type"
            testId="radio-auth"
            isDisabled={!editing}
            isChecked={values.auth_type === 'pat'}
            onChange={onChange}
          />
        </div>

        {values.auth_type === 'basic' && (
          <>
            <Field label="Jira Integration user login" isRequired errorMessage={usernameSameAsCurrent && "Don't use your credentials. Create new integration user instead."}>
              <TextField
                isDisabled={!editing}
                isCompact
                value={values.username}
                onChange={onChange}
                name="username"
              />
            </Field>
            <Field label="Jira Integration user password" isRequired>
              <TextField
                isCompact
                type="password"
                isDisabled={!editing}
                value={values.password}
                onChange={onChange}
                name="password"
              />
            </Field>
          </>
        )}

        {values.auth_type === 'pat' && (
          <Field label="Jira Personal Access Token (PAT)" isRequired>
            <TextField
              isCompact
              type="password"
              isDisabled={!editing}
              value={values.pat}
              onChange={onChange}
              name="pat"
            />
          </Field>
        )}

        <div style={{ height: 10 }} />
        {!editing && <JiraCredentialsValidation />}
        {requiredFieldsMissing
        && <ValidationMessage message="Please fill in all required fields" color={token('color.text.danger')} />}
        {error && <ErrorMessage error={error} />}
        <div style={{ height: 10 }} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
          <ButtonGroup>
            {!editing && <Button onClick={onEdit} appearance="primary">Edit</Button>}
            {editing && <Button onClick={onCancel}>Cancel</Button>}
            {editing && <LoadingButton type="submit" appearance="primary" isLoading={saving}>Save</LoadingButton>}
          </ButtonGroup>
        </div>
      </form>
    </div>
  );
};

export default JiraCredentialsForm;
